<template>
  <div class="uh-shell">
    <!-- Header -->
    <header class="uh-header">
      <div>
        <div class="eyebrow">Uploads</div>
        <h1>Upload History</h1>
        <p class="subtitle">Every bank-statement upload — see match counts and drill into per-row results.</p>
      </div>
    </header>

    <!-- Filters -->
    <section class="uh-card uh-filters">
      <div class="search-wrap">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
        </svg>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Search by bank, uploader, or file name…"
          class="search-input"
        />
        <button
          v-if="filters.search"
          type="button"
          class="search-clear"
          @click="filters.search = ''"
          title="Clear"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="chip-group">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': filters.status === opt.value }"
          @click="filters.status = opt.value"
        >{{ opt.label }}</button>
      </div>

      <div class="date-pair">
        <label class="date-pill">
          <span class="date-pill-label">From</span>
          <input type="date" v-model="filters.startDate" class="date-pill-input" />
        </label>
        <label class="date-pill">
          <span class="date-pill-label">To</span>
          <input type="date" v-model="filters.endDate" class="date-pill-input" />
        </label>
      </div>

      <button
        v-if="hasActiveFilters"
        type="button"
        class="reset-btn"
        @click="resetFilters"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
        </svg>
        Reset
      </button>
    </section>

    <!-- Table -->
    <section class="uh-card">
      <div class="card-head">
        <div class="card-title">
          All uploads
          <span class="head-count">{{ pagination.total }}</span>
        </div>
        <div v-if="pagination.total > 0" class="card-pag-info">
          Showing {{ pagination.firstItem }}–{{ pagination.lastItem }} of {{ pagination.total }}
        </div>
      </div>

      <div v-if="isLoading" class="uh-loading">
        <div class="loading-spinner"></div>
        <div class="loading-label">Loading upload history…</div>
      </div>

      <div v-else-if="!history.length" class="empty-hero">
        <div class="empty-hero-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke-linejoin="round" />
            <path d="M14 2v6h6" stroke-linejoin="round" />
            <path d="M12 12v6M9 15l3 3 3-3" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <div class="empty-hero-title">{{ hasActiveFilters ? 'No uploads match these filters' : 'No uploads yet' }}</div>
        <div class="empty-hero-sub">
          {{ hasActiveFilters ? 'Try clearing filters to see everything.' : 'Uploaded statements will appear here with their verification results.' }}
        </div>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="btn btn-ghost btn-inline"
          @click="resetFilters"
        >Reset filters</button>
      </div>

      <div v-else class="table-wrap">
        <table class="uh-table">
          <thead>
            <tr>
              <th>Bank</th>
              <th>Uploaded by</th>
              <th>Date</th>
              <th>Status</th>
              <th class="th-num">Verified</th>
              <th class="th-num">Unmatched</th>
              <th class="th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in history" :key="item.id">
              <td class="td-strong">{{ item.bank_username }}</td>
              <td class="td-mini">{{ item.uploaded_by_username }}</td>
              <td class="td-mini">{{ formatDate(item.created_at) }}</td>
              <td>
                <span class="pill" :class="item.status === 'success' ? 'pill-success' : 'pill-danger'">
                  {{ item.status }}
                </span>
              </td>
              <td class="td-num td-verified">{{ item.total_verified }}</td>
              <td class="td-num td-unmatched" :class="{ 'td-unmatched-warn': (item.total_unmatched || 0) > 0 }">
                {{ item.total_unmatched }}
              </td>
              <td class="td-actions">
                <button v-if="can('payin.statement.download')" type="button" class="action-btn action-raw" @click="downloadRaw(item.id)" title="Download raw file">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Raw
                </button>
                <button type="button" class="action-btn action-view" @click="viewResult(item.id)" title="View match results">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="!isLoading && history.length" class="pag-bar">
        <div class="pag-info">
          Page <b>{{ pagination.page }}</b> of {{ pagination.lastPage }}
        </div>
        <div class="pag-controls">
          <button
            type="button"
            class="pag-btn"
            :disabled="pagination.page <= 1"
            @click="goToPage(pagination.page - 1)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Prev
          </button>
          <button
            type="button"
            class="pag-btn"
            :disabled="!pagination.hasMorePages"
            @click="goToPage(pagination.page + 1)"
          >
            Next
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </section>

    <!-- Result modal -->
    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="showResultDialog" class="modal-backdrop" @click.self="closeResultDialog">
          <div class="modal-card">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">Upload result</div>
                <div class="modal-title">Bank Statement Parsing Result</div>
              </div>
              <button type="button" class="modal-close" @click="closeResultDialog" aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
                </svg>
              </button>
            </div>

            <div class="modal-body">
              <!-- Summary chips -->
              <div class="chip-row">
                <span class="stat-chip stat-chip-success">
                  <span class="chip-dot"></span>
                  Verified <b>{{ (resultData.verified || []).length }}</b>
                </span>
                <span class="stat-chip stat-chip-danger">
                  <span class="chip-dot"></span>
                  Unmatched <b>{{ (resultData.unmatched || []).length }}</b>
                </span>
                <span class="stat-chip stat-chip-warn">
                  <span class="chip-dot"></span>
                  Skipped <b>{{ (resultData.skipped || []).length }}</b>
                </span>
              </div>

              <!-- Verified -->
              <div v-if="resultData.verified && resultData.verified.length" class="bucket bucket-success" :class="{ 'is-open': bucketOpen.verified }">
                <button type="button" class="bucket-summary" @click="toggleBucket('verified')">
                  <span class="bucket-dot"></span>
                  <span class="bucket-title">Verified orders</span>
                  <span class="bucket-count">{{ resultData.verified.length }}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
                    <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
                <div v-show="bucketOpen.verified" class="bucket-body">
                  <div class="table-wrap">
                    <table class="uh-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th class="th-num">Amount</th>
                          <th>UTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in resultData.verified" :key="'v-' + idx">
                          <td class="td-strong">{{ item.merchantOrderId }}</td>
                          <td class="td-num td-verified">₹{{ formatMoney(item.amount) }}</td>
                          <td class="td-mono">{{ item.utr }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Unmatched -->
              <div v-if="resultData.unmatched && resultData.unmatched.length" class="bucket bucket-danger" :class="{ 'is-open': bucketOpen.unmatched }">
                <button type="button" class="bucket-summary" @click="toggleBucket('unmatched')">
                  <span class="bucket-dot"></span>
                  <span class="bucket-title">Unmatched rows</span>
                  <span class="bucket-count">{{ resultData.unmatched.length }}</span>
                  <span class="bucket-sub">statement entries with no matching order</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
                    <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
                <div v-show="bucketOpen.unmatched" class="bucket-body">
                  <div class="table-wrap">
                    <table class="uh-table">
                      <thead>
                        <tr>
                          <th class="th-num">Amount</th>
                          <th>UTR</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in resultData.unmatched" :key="'u-' + idx">
                          <td class="td-num td-strong">₹{{ formatMoney(item.amount) }}</td>
                          <td class="td-mono">{{ item.utr }}</td>
                          <td class="td-mini">{{ item.reason }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Skipped -->
              <div v-if="resultData.skipped && resultData.skipped.length" class="bucket bucket-warn" :class="{ 'is-open': bucketOpen.skipped }">
                <button type="button" class="bucket-summary" @click="toggleBucket('skipped')">
                  <span class="bucket-dot"></span>
                  <span class="bucket-title">Skipped rows</span>
                  <span class="bucket-count">{{ resultData.skipped.length }}</span>
                  <span class="bucket-sub">rows that couldn't be parsed</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
                    <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
                <div v-show="bucketOpen.skipped" class="bucket-body">
                  <div class="table-wrap">
                    <table class="uh-table">
                      <thead>
                        <tr>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in resultData.skipped" :key="'s-' + idx">
                          <td class="td-mini">{{ item.reason }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div
                v-if="!(resultData.verified && resultData.verified.length) &&
                      !(resultData.unmatched && resultData.unmatched.length) &&
                      !(resultData.skipped && resultData.skipped.length)"
                class="empty-mini"
              >No details available for this upload.</div>
            </div>

            <div class="modal-foot">
              <button type="button" class="btn btn-ghost" @click="closeResultDialog">Close</button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  data() {
    return {
      history: [],
      isLoading: true, // guards the "no uploads" empty-state flash on first paint
      showResultDialog: false,
      resultData: { verified: [], unmatched: [], skipped: [] },
      // Per-bucket open/close state for the result modal. Vue-controlled
      // instead of native <details> so each bucket is guaranteed independent
      // (native was intermittently swallowing the open state on multi-open).
      bucketOpen: { verified: true, unmatched: true, skipped: false },

      // Filters + pagination
      filters: {
        search: "",
        status: "all",
        startDate: "",
        endDate: "",
      },
      statusOptions: [
        { value: "all", label: "All" },
        { value: "success", label: "Success" },
        { value: "failed", label: "Failed" },
      ],
      pagination: {
        page: 1,
        perPage: 20,
        total: 0,
        lastPage: 1,
        firstItem: 0,
        lastItem: 0,
        hasMorePages: false,
      },
      // debounce timer id for the search box
      searchTimer: null,
    };
  },
  computed: {
    hasActiveFilters() {
      const f = this.filters;
      return (
        (f.search && f.search.trim() !== "") ||
        (f.status && f.status !== "all") ||
        !!f.startDate ||
        !!f.endDate
      );
    },
  },
  watch: {
    "filters.search"() {
      // Debounce so we don't hit the backend on every keystroke.
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.loadHistory();
      }, 300);
    },
    "filters.status"() { this.pagination.page = 1; this.loadHistory(); },
    "filters.startDate"() { this.pagination.page = 1; this.loadHistory(); },
    "filters.endDate"() { this.pagination.page = 1; this.loadHistory(); },
  },
  async mounted() {
    await this.loadHistory();
  },
  methods: {
    can(perm) { return this.$store.getters.hasPermission(perm); },
    async loadHistory() {
      this.isLoading = true;
      try {
        const params = new URLSearchParams();
        params.set("page", this.pagination.page);
        params.set("perPage", this.pagination.perPage);
        if (this.filters.status && this.filters.status !== "all") params.set("status", this.filters.status);
        if (this.filters.search && this.filters.search.trim()) params.set("search", this.filters.search.trim());
        if (this.filters.startDate) params.set("startDate", this.filters.startDate);
        if (this.filters.endDate) params.set("endDate", this.filters.endDate);

        const { data } = await http.get(`/orders/upload-history?${params.toString()}`);
        this.history = (data && data.data) || [];
        if (data && data.pagination) {
          this.pagination = {
            page: data.pagination.page || 1,
            perPage: data.pagination.perPage || this.pagination.perPage,
            total: data.pagination.total || 0,
            lastPage: data.pagination.lastPage || 1,
            firstItem: data.pagination.firstItem || 0,
            lastItem: data.pagination.lastItem || 0,
            hasMorePages: !!data.pagination.hasMorePages,
          };
        }
      } catch (e) {
        this.history = [];
        this.pagination = { ...this.pagination, total: 0, lastPage: 1, firstItem: 0, lastItem: 0, hasMorePages: false };
      } finally {
        this.isLoading = false;
      }
    },
    goToPage(p) {
      if (p < 1 || p > this.pagination.lastPage) return;
      this.pagination.page = p;
      this.loadHistory();
    },
    resetFilters() {
      this.filters = { search: "", status: "all", startDate: "", endDate: "" };
      this.pagination.page = 1;
      this.loadHistory();
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    formatMoney(n) {
      const v = Number(n) || 0;
      return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    },
    downloadRaw(id) {
      // Build from the axios instance's RESOLVED baseURL, not the raw
      // VUE_APP_API_URL env: http-common overrides the base per hostname
      // (localhost → :3000, indiapayu.com → bc.indiapayu.com), and the
      // raw env may even be undefined locally — which produced
      // "undefined/orders/…" download URLs. window.open still carries the
      // auth cookie, so the checkAuth-protected endpoint works.
      window.open(`${http.defaults.baseURL}/orders/upload-history/${id}/download-raw`, '_blank');
    },
    async viewResult(id) {
      try {
        const { data } = await http.get(`/orders/upload-history/${id}/result`);
        this.resultData = data.data;
        // Fresh open — reset to a sensible default: expand the two
        // "content" buckets, collapse the skipped one.
        this.bucketOpen = { verified: true, unmatched: true, skipped: false };
        this.showResultDialog = true;
      } catch (error) {
        alert('Failed to load result');
      }
    },
    closeResultDialog() {
      this.showResultDialog = false;
      this.resultData = { verified: [], unmatched: [], skipped: [] };
    },
    toggleBucket(name) {
      this.bucketOpen = { ...this.bucketOpen, [name]: !this.bucketOpen[name] };
    },
  },
};
</script>

<style scoped>
/* Shell */
.uh-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* Header */
.uh-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.uh-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
}

/* Card */
.uh-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.card-head {}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.head-count {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 999px;
}

/* Empty */
.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}
.empty-hero-icon {
  width: 54px; height: 54px;
  border-radius: 14px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 28px; height: 28px; }
.empty-hero-title { font-size: 15px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 12px; color: #64748b; }

.empty-mini {
  padding: 20px 16px;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
}

/* ==================== Filters ==================== */
.uh-filters {
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.search-input:focus { border-color: #1B2A47; background: #fff; }
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

.chip-group { display: flex; gap: 4px; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}

/* Date pair — inline label + date input inside a single pill so every
   filter element sits on the same baseline as the search + chip group. */
.date-pair { display: flex; gap: 8px; flex-wrap: wrap; }
.date-pill {
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  cursor: text;
  margin-bottom: 0;
}
.date-pill:focus-within {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.date-pill-label {
  padding: 7px 10px;
  font-size: 10px;
  color: #64748b;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  border-right: 1px solid #eef2f7;
}
.date-pill-input {
  padding: 7px 10px;
  border: none;
  background: transparent;
  font-size: 12px;
  color: #0f172a;
  outline: none;
  font-family: inherit;
  min-width: 120px;
}

.reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
}
.reset-btn svg { width: 13px; height: 13px; }
.reset-btn:hover { background: #fee2e2; }

/* Pagination bar */
.pag-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
  flex-wrap: wrap;
  gap: 10px;
}
.pag-info {
  font-size: 12px;
  color: #64748b;
}
.pag-info b {
  color: #0f172a;
  font-weight: 700;
}
.pag-controls { display: inline-flex; gap: 6px; }
.pag-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
}
.pag-btn svg { width: 13px; height: 13px; }
.pag-btn:hover:not(:disabled) {
  border-color: #cbd5e1;
  color: #1B2A47;
  background: #eef2f7;
}
.pag-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Card head pagination-info hint */
.card-pag-info {
  font-size: 11px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

/* Ghost btn used inside empty state */
.btn-inline { margin-top: 8px; }

/* Loading state (initial fetch) */
.uh-loading {
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

/* Table */
.table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
}
.uh-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.uh-table thead { background: #f8fafc; }
.uh-table th {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.th-num { text-align: right; }
.th-actions { text-align: right; }
.uh-table tbody tr {
  transition: background 0.12s;
}
.uh-table tbody tr:nth-child(even) { background: #fafbfc; }
.uh-table tbody tr:hover { background: #eef2f7; }
.uh-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
  vertical-align: middle;
}
.uh-table tbody tr:last-child td { border-bottom: none; }
.td-num { text-align: right; font-variant-numeric: tabular-nums; }
.td-strong { font-weight: 600; }
.td-mono { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 11px; color: #475569; }
.td-mini { font-size: 12px; color: #64748b; }
.td-verified { color: #047857; font-weight: 600; }
.td-unmatched { color: #64748b; }
.td-unmatched-warn { color: #b45309; font-weight: 700; }
.td-actions {
  text-align: right;
  white-space: nowrap;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

/* Pills */
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.pill-success { background: #d1fae5; color: #065f46; }
.pill-danger  { background: #fee2e2; color: #991b1b; }

/* Action buttons */
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  color: #475569;
  transition: all 0.12s;
}
.action-btn svg { width: 13px; height: 13px; }
.action-btn:hover { border-color: #cbd5e1; background: #f8fafc; }
.action-raw:hover  { color: #1B2A47; border-color: #cbd5e1; background: #eef2f7; }
.action-view:hover { color: #047857; border-color: #a7f3d0; background: #ecfdf5; }

/* Modal */
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  z-index: 50;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px -20px rgba(15, 23, 42, 0.4);
}
.modal-head {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
}
.modal-eyebrow { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: #1B2A47; font-weight: 700; }
.modal-title { font-size: 18px; font-weight: 700; color: #0f172a; margin-top: 4px; }
.modal-close {
  border: none; background: transparent; cursor: pointer;
  width: 32px; height: 32px; border-radius: 8px; color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.modal-close:hover { background: #f1f5f9; color: #475569; }
.modal-close svg { width: 18px; height: 18px; }

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.modal-foot {
  padding: 14px 24px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: #f8fafc;
}

/* Summary chips */
.chip-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
}
.stat-chip b { font-weight: 700; margin-left: 2px; font-variant-numeric: tabular-nums; }
.chip-dot { width: 6px; height: 6px; border-radius: 999px; display: inline-block; }
.stat-chip-success { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
.stat-chip-success .chip-dot { background: #10b981; }
.stat-chip-danger  { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.stat-chip-danger  .chip-dot { background: #ef4444; }
.stat-chip-warn    { background: #fffbeb; color: #92400e; border-color: #fde68a; }
.stat-chip-warn    .chip-dot { background: #f59e0b; }

/* Buckets — Vue-controlled expand/collapse (not native <details>) so each
   bucket's open state is independently reactive and never gets swallowed
   by browser-level state weirdness. */
.bucket {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left-width: 4px;
  border-radius: 12px;
  overflow: hidden;
}
.bucket-success { border-left-color: #10b981; }
.bucket-danger  { border-left-color: #ef4444; }
.bucket-warn    { border-left-color: #f59e0b; }
.bucket-summary {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  font-weight: 600;
  color: #0f172a;
  font-size: 13px;
  user-select: none;
  transition: background 0.12s;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  font-family: inherit;
}
.bucket-summary:hover { background: #f8fafc; }
.bucket-summary:focus-visible {
  outline: 2px solid #1B2A47;
  outline-offset: -2px;
}
.bucket-dot {
  width: 7px; height: 7px;
  border-radius: 999px;
  display: inline-block;
}
.bucket-success .bucket-dot { background: #10b981; }
.bucket-danger  .bucket-dot { background: #ef4444; }
.bucket-warn    .bucket-dot { background: #f59e0b; }
.bucket-title { font-weight: 700; }
.bucket-count {
  background: #f1f5f9;
  color: #475569;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
}
.bucket-sub {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  margin-left: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bucket-chevron {
  width: 13px; height: 13px;
  color: #94a3b8;
  margin-left: auto;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.bucket.is-open .bucket-chevron { transform: rotate(90deg); }
.bucket-body {
  padding: 0 16px 16px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-ghost:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }

/* Modal transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.18s ease; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .modal-card, .modal-fade-leave-to .modal-card {
  transform: translateY(20px) scale(0.98);
}
</style>
